import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";

import { useEnabledPaymentMethods } from "checkout-experience/hooks/use-enabled-payment-methods";
import { PaymentType } from "checkout-query-client/types/form-types";
import { loadScript } from "checkout-query-client/utils/load-script";

type PaymentScriptProviderProps = PropsWithChildren & {
  data?: string;
};

export type PaymentProvidersTypes = {
  [key in PaymentType]: {
    isLoaded: boolean;
    isError: boolean;
    isLoading: boolean;
  };
};

export type PartialPaymentProvidersTypes = {
  [key in PaymentType]?: {
    isLoaded: boolean;
    isError: boolean;
    isLoading: boolean;
  };
};

export type PaymentScriptProviderContextType = {
  paymentProviders: PaymentProvidersTypes;
  setPaymentProviders: Dispatch<SetStateAction<PaymentProvidersTypes>>;
};

export const initialLoadState = {
  isLoaded: false,
  isError: false,
  isLoading: true,
};

export const initialLoadStateForLocal = {
  isLoaded: true,
  isError: false,
  isLoading: false,
};

export const initialValueForPaymentContext = {
  [PaymentType.googlePay]: { ...initialLoadState },
  [PaymentType.applePay]: { ...initialLoadState },
  [PaymentType.creditCard]: { ...initialLoadStateForLocal },
  [PaymentType.eCheck]: { ...initialLoadStateForLocal },
  [PaymentType.sepa]: { ...initialLoadStateForLocal },
  [PaymentType.payByBank]: { ...initialLoadState },
  [PaymentType.paze]: { ...initialLoadState },
  [PaymentType.ideal]: { ...initialLoadStateForLocal },
  [PaymentType.alipay]: { ...initialLoadStateForLocal },
  [PaymentType.paypal]: { ...initialLoadStateForLocal },
  [PaymentType.wechatPay]: { ...initialLoadStateForLocal },
  [PaymentType.trustly]: { ...initialLoadStateForLocal },
  [PaymentType.giftCard]: { ...initialLoadStateForLocal },
};

export const PaymentScriptProviderContext =
  createContext<PaymentScriptProviderContextType>({
    paymentProviders: initialValueForPaymentContext,
    setPaymentProviders: () => {},
  });

// <PaymentScriptProvider /> is concerned with the load state of the scripts, not whether it is actually functional on the DOM.  We will rely on useEnabledPaymentMethods for that.
export const PaymentScriptProvider = ({
  children,
}: PaymentScriptProviderProps) => {
  const [paymentProviders, setPaymentProviders] =
    useState<PaymentProvidersTypes>(initialValueForPaymentContext);
  const [isPaymentProvidersNotEnabledSet, setIsPaymentProvidersNotEnabledSet] =
    useState(false);
  const { paymentMethodsList, isPaymentMethodsListInitialized } =
    useEnabledPaymentMethods();

  const handleSetPaymentMethodLoad = () => {
    return (cb: () => void) => {
      cb();
    };
  };

  const handleSetPaymentMethodLoadSuccess = (name: PaymentType) => {
    return () => {
      setPaymentProviders((oldPaymentProviders: PaymentProvidersTypes) => ({
        ...oldPaymentProviders,
        [name]: { isLoaded: true, isError: false, isLoading: true }, // the activate is the last part of the load. useEnabledPaymentMethods() will override the isLoading to false
      }));
    };
  };

  const handleSetPaymentMethodLoadError = (name: PaymentType) => {
    return () => {
      setPaymentProviders((oldPaymentProviders: PaymentProvidersTypes) => ({
        ...oldPaymentProviders,
        [name]: { isLoaded: false, isError: true, isLoading: false },
      }));
    };
  };

  const handleSetPaymentMethodNotEnabled = (
    paymentProvidersNotEnabled: PartialPaymentProvidersTypes
  ) => {
    setPaymentProviders((oldPaymentProviders: PaymentProvidersTypes) => ({
      ...oldPaymentProviders,
      ...paymentProvidersNotEnabled,
    }));
    setIsPaymentProvidersNotEnabledSet(true);

    // load the scripts
    paymentMethodsList.forEach((method) => {
      if (method.script) {
        loadScript({
          script: method.script,
          onLoad: handleSetPaymentMethodLoad(),
          onSuccess: handleSetPaymentMethodLoadSuccess(method.name),
          onError: handleSetPaymentMethodLoadError(method.name),
        });
      }
    });
  };

  useEffect(() => {
    // mark payment providers that are not enabled
    if (
      isPaymentMethodsListInitialized &&
      !isPaymentProvidersNotEnabledSet &&
      paymentMethodsList?.length > 0 &&
      paymentProviders
    ) {
      const paymentProvidersNotEnabled: PartialPaymentProvidersTypes = {};
      Object.keys(paymentProviders).forEach((paymentProvider) => {
        const hasMatchingPaymentMethod = paymentMethodsList.some(
          (method) => method.name === (paymentProvider as PaymentType)
        );
        if (!hasMatchingPaymentMethod) {
          paymentProvidersNotEnabled[paymentProvider as PaymentType] = {
            isLoaded: false,
            isError: false,
            isLoading: false,
          };
        }
      });
      handleSetPaymentMethodNotEnabled(paymentProvidersNotEnabled);
    }
  }, [paymentMethodsList, isPaymentMethodsListInitialized]);

  return (
    <PaymentScriptProviderContext.Provider
      value={{ paymentProviders, setPaymentProviders }}
    >
      <span data-testid="paymentScriptProviderWrapper">{children}</span>
    </PaymentScriptProviderContext.Provider>
  );
};
